import TonWeb from 'tonweb';
import axios from 'axios';
import BigNumber from 'bignumber.js';
import { tonRouter } from '../../../connectors/TonConnector';
import { PROXY_TON } from './nativeCoins';
import { tonChain } from '../../../helpers/customChains';

const getTonTxParams = async (
  walletAddress: string,
  jetton0: string,
  jetton1: string,
  amount: string,
  minAskAmount: string
) => {
  if (jetton0 === PROXY_TON) {
    // transaction to swap [amount] JETTON0 to JETTON1 but not less than 1 nano JETTON1
    const swapTxParams = await tonRouter.buildSwapProxyTonTxParams({
      // address of the wallet that holds offerJetton you want to swap
      userWalletAddress: walletAddress,
      // address of the jetton you want to swap
      proxyTonAddress: PROXY_TON,
      // amount of the jetton you want to swap
      offerAmount: amount,
      // address of the jetton you want to receive
      askJettonAddress: jetton1,
      // minimal amount of the jetton you want to receive as a result of the swap.
      // If the amount of the jetton you want to receive is less than minAskAmount
      // the transaction will bounce
      minAskAmount: minAskAmount,
      // query id to identify your transaction in the blockchain (optional)
      // queryId: 12345,
      // address of the wallet to receive the referral fee (optional)
      // referralAddress: undefined,
    });
    return swapTxParams;
  }
  const swapTxParams = await tonRouter.buildSwapJettonTxParams({
    // address of the wallet that holds offerJetton you want to swap
    userWalletAddress: walletAddress,
    // address of the jetton you want to swap
    offerJettonAddress: jetton0,
    // amount of the jetton you want to swap
    offerAmount: amount,
    // address of the jetton you want to receive
    askJettonAddress: jetton1,
    // minimal amount of the jetton you want to receive as a result of the swap.
    // If the amount of the jetton you want to receive is less than minAskAmount
    // the transaction will bounce
    minAskAmount: minAskAmount,
    // query id to identify your transaction in the blockchain (optional)
    // queryId: 12345,
    // address of the wallet to receive the referral fee (optional)
    // referralAddress: undefined,
  });
  return swapTxParams;
};

interface IRatesResponse {
  rates: {
    [key: string]: {
      prices: {
        USD: number;
      };
      diff_24h: {
        USD: string;
      };
      diff_7d: {
        USD: string;
      };
      diff_30d: {
        USD: string;
      };
    };
  };
}

const tonTokensRatesUrl = 'https://tonapi.io/v2/rates';

const getTonTokensRates = async (
  tokens: string[],
  controller: AbortController
) => {
  try {
    const params = {
      tokens: tokens.join(','),
      currencies: 'usd',
    };
    const { data } = await axios.get<IRatesResponse>(tonTokensRatesUrl, {
      params,
      signal: controller.signal,
    });
    return data.rates;
  } catch (error) {
    console.warn(error);
    return null;
  }
};

export interface ITonSwapSimulateResponse {
  ask_address: string;
  ask_units: string;
  fee_address: string;
  fee_percent: string;
  fee_units: string;
  min_ask_units: string;
  offer_address: string;
  offer_units: string;
  pool_address: string;
  price_impact: string;
  router_address: string;
  slippage_tolerance: string;
  swap_rate: string;
}

const simulateTonSwapUrl = 'https://api.ston.fi/v1/swap/simulate';

const simulateTonSwap = async (
  offerAddress: string,
  askAddress: string,
  amount: string,
  slippage: number,
  controller: AbortController
) => {
  try {
    const params = {
      offer_address: offerAddress,
      ask_address: askAddress,
      units: amount,
      slippage_tolerance: slippage,
    };
    const { data } = await axios.post<ITonSwapSimulateResponse>(
      simulateTonSwapUrl,
      {},
      { params, signal: controller.signal }
    );
    return data;
  } catch (error) {
    console.warn(error);
    return null;
  }
};

interface ITonTxHashResponse {
  account: string;
  lt: number;
  hash: string;
  utime: number;
  fee: number;
  storage_fee: number;
  other_fee: number;
  transaction_type: string;
  compute_skip_reason: null;
  compute_exit_code: number;
  compute_gas_used: number;
  compute_gas_limit: number;
  compute_gas_credit: number;
  compute_gas_fees: number;
  compute_vm_steps: number;
  action_result_code: number;
  action_total_fwd_fees: number;
  action_total_action_fees: number;
  in_msg: {
    source: string;
    destination: string;
    value: number;
    fwd_fee: number;
    ihr_fee: number;
    created_lt: number;
    op: number;
    comment: null;
    hash: string;
    body_hash: string;
    body: null;
  };
  out_msgs: {
    source: string;
    destination: string;
    value: number;
    fwd_fee: number;
    ihr_fee: number;
    created_lt: number;
    op: number;
    comment: null;
    hash: string;
    body_hash: string;
    body: null;
  }[];
}

const tonTxHashUrl =
  'https://toncenter.com/api/index/getTransactionByInMessageHash';

const getTonTxHash = async (msgHash: string) => {
  try {
    const params = {
      msg_hash: msgHash.replaceAll('_', '/'),
    };
    const { data } = await axios.get<ITonTxHashResponse[]>(tonTxHashUrl, {
      params,
    });
    return data[0]?.hash || '';
  } catch (error) {
    console.warn(error);
    return '';
  }
};

export interface ITonTransactionStatusResponse {
  success: boolean;
  aborted: boolean;
  destroyed: boolean;
}

const tonTransactionUrl = 'https://tonapi.io/v2/blockchain/transactions/';

const getTonTransaction = async (hash: string) => {
  const { data } = await axios.get<ITonTransactionStatusResponse>(
    tonTransactionUrl + hash
  );
  return data;
};

interface ITonFeesResponse {
  '@type': string;
  source_fees: {
    in_fwd_fee: number;
    storage_fee: number;
    gas_fee: number;
    fwd_fee: number;
  };
  destination_fees: any[]; // This can be an empty array or have its own structure
  '@extra': string;
}

const estimateTonFees = async (
  walletAddress: string,
  jetton0: string,
  jetton1: string,
  amount: string,
  minAskAmount: string
) => {
  try {
    const txParams = await getTonTxParams(
      walletAddress,
      jetton0,
      jetton1,
      amount,
      minAskAmount
    );
    const data: ITonFeesResponse = await tonRouter.provider.getEstimateFee({
      address: txParams.to.toString(true, true, false),
      body: TonWeb.utils.bytesToBase64(await txParams.payload.toBoc(false)),
    });
    return BigNumber(data.source_fees.storage_fee)
      .plus(data.source_fees.fwd_fee)
      .plus(data.source_fees.in_fwd_fee)
      .plus(tonRouter.gasConstants.swap.toString())
      .div(100)
      .div(10 ** tonChain.nativeCurrency.decimals)
      .toString();
  } catch (error) {
    console.warn(error);
    return '0';
  }
};

export {
  getTonTxParams,
  getTonTokensRates,
  simulateTonSwap,
  getTonTxHash,
  getTonTransaction,
  estimateTonFees,
};
